import useDebounceCallback from "hooks/useDebounceCallback";
import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getAnyaAssistant, getAnyaMessage } from "restAPI/AnyaAPI";
import { getAnyaAgents } from "restAPI/AnyaAgentsAPI";
import { AnyaAgentSummary } from "views/Automations/AnyaAgents/types";
import { getErrorMessage } from "views/Automations/AnyaAgents/utils";
import { ANYA_POLLING_CONFIG } from "views/Dashboards/anyaConstants";
import {
  isResponseCompleted,
  isStatusInProgress,
} from "views/Dashboards/anyaUtils";

type AnyaAgentExecutionResponse = {
  reasoningSummary?: string;
  details?: string;
  status?: string;
  requestId?: string;
  conversationId?: string;
};

const userLocale = navigator.language || "en-US";
const userZoneId = moment.tz.guess();
const FINDING_ANYA_AGENTS_PAGE_SIZE = 5;

function dedupeAgents(agents: Array<AnyaAgentSummary | null | undefined>) {
  const dedupedAgents: AnyaAgentSummary[] = [];

  agents.forEach((agent) => {
    if (!agent) {
      return;
    }

    if (
      !dedupedAgents.find(
        (existingAgent) => String(existingAgent.id) === String(agent.id),
      )
    ) {
      dedupedAgents.push(agent);
    }
  });

  return dedupedAgents;
}

export default function useFindingAnyaAgentsTab(findingId?: string | number) {
  const [searchTerm, setSearchTerm] = useState("");
  const [agentsRefreshKey, setAgentsRefreshKey] = useState(0);
  const [agents, setAgents] = useState<AnyaAgentSummary[]>([]);
  const [defaultAgent, setDefaultAgent] = useState<AnyaAgentSummary | null>(
    null,
  );
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [agentsError, setAgentsError] = useState("");
  const [selectedAgent, setSelectedAgent] = useState<AnyaAgentSummary | null>(
    null,
  );
  const [agentResponse, setAgentResponse] =
    useState<AnyaAgentExecutionResponse | null>(null);
  const [agentResponseLoading, setAgentResponseLoading] = useState(false);
  const [agentResponseError, setAgentResponseError] = useState("");
  const [agentResponseExists, setAgentResponseExists] = useState(true);

  useEffect(() => {
    setAgentsLoading(true);
    setAgentsError("");

    const request = getAnyaAgents({
      page: 0,
      size: FINDING_ANYA_AGENTS_PAGE_SIZE,
      search: searchTerm.trim() || undefined,
    });

    request.promise
      .then((response) => {
        const data = response?.data;
        setAgents(Array.isArray(data?.content) ? data.content : []);
        setDefaultAgent(data?.default_agent ?? null);
      })
      .catch((fetchError) => {
        setAgents([]);
        setDefaultAgent(null);
        setAgentsError(
          getErrorMessage(
            fetchError,
            "Failed to load Anya agents. Please try again.",
          ),
        );
      })
      .finally(() => {
        setAgentsLoading(false);
      });

    return request.cancel;
  }, [agentsRefreshKey, searchTerm]);

  useEffect(
    function resetAgentStateOnFindingChange() {
      setSelectedAgent(null);
      setAgentResponse(null);
      setAgentResponseError("");
      setAgentResponseExists(true);
    },
    [findingId],
  );

  useEffect(
    function syncSelectedAgentWithDefault() {
      if (!selectedAgent && defaultAgent) {
        setSelectedAgent(defaultAgent);
      }
    },
    [defaultAgent, selectedAgent],
  );

  const debouncedSearchAgents = useDebounceCallback(
    (value: string) => {
      setSearchTerm(value);
    },
    [],
    300,
  );

  const updateSearch = useCallback(
    (value: string) => {
      if (value.trim() !== searchTerm.trim()) {
        setAgentsLoading(true);
      }
      debouncedSearchAgents(value);
    },
    [debouncedSearchAgents, searchTerm],
  );

  const agentOptions = useMemo(
    () => dedupeAgents([selectedAgent, defaultAgent, ...agents]),
    [agents, defaultAgent, selectedAgent],
  );

  const refreshAgents = useCallback(() => {
    setAgentsRefreshKey((currentKey) => currentKey + 1);
  }, []);

  const selectAgent = useCallback(
    (agentId: string | number) => {
      const nextAgent = agentOptions.find(
        (agent) => String(agent.id) === String(agentId),
      );

      if (nextAgent) {
        setSelectedAgent(nextAgent);
      }
    },
    [agentOptions],
  );

  useEffect(
    function loadSelectedAgentResponse() {
      if (!findingId || !selectedAgent) {
        setAgentResponse(null);
        setAgentResponseLoading(false);
        setAgentResponseError("");
        setAgentResponseExists(true);
        return;
      }

      let activeRequestCancel = () => {};
      let pollTimeoutId: ReturnType<typeof setTimeout> | null = null;
      let isDisposed = false;
      const pollingStartTime = Date.now();

      setAgentResponse(null);
      setAgentResponseLoading(true);
      setAgentResponseError("");
      setAgentResponseExists(true);

      const handleResponseComplete = (response: AnyaAgentExecutionResponse) => {
        if (isDisposed) {
          return;
        }

        const hasContent = response?.details;
        setAgentResponse(hasContent ? response : null);
        setAgentResponseExists(Boolean(hasContent));
        setAgentResponseLoading(false);
      };

      const waitForDelay = (delay: number) =>
        new Promise<void>((resolve) => {
          pollTimeoutId = setTimeout(() => {
            pollTimeoutId = null;
            resolve();
          }, delay);
        });

      const pollUntilComplete = async (
        conversationId: string,
        requestId: string,
      ) => {
        while (!isDisposed) {
          if (
            Date.now() - pollingStartTime >
            ANYA_POLLING_CONFIG.MAX_POLL_TIME
          ) {
            setAgentResponseError("Request timed out. Please try again.");
            setAgentResponseLoading(false);
            return;
          }

          const pollRequest = getAnyaMessage({
            conversationId,
            messageId: requestId,
          });
          activeRequestCancel = pollRequest.cancel;

          try {
            const result = await pollRequest.promise;
            const response = result?.data || result;

            if (isDisposed) {
              return;
            }

            if (isResponseCompleted(response)) {
              handleResponseComplete(response);
              return;
            }
          } catch (error: any) {
            if (!error?.cancelled && !isDisposed) {
              setAgentResponseError("Failed to load agent output");
              setAgentResponseLoading(false);
            }
            return;
          }

          await waitForDelay(ANYA_POLLING_CONFIG.SUBSEQUENT_POLL_INTERVAL);

          if (isDisposed) {
            return;
          }
        }
      };

      const getAgentResponse = async () => {
        const request = getAnyaAssistant({
          query: `${findingId}`,
          conversation_history: [],
          widgetTitle: "",
          widgetType: String(findingId),
          dashboardId: String(findingId),
          locale: userLocale,
          zoneId: userZoneId,
          agentId: selectedAgent.id,
        });
        activeRequestCancel = request.cancel;

        try {
          const result = await request.promise;

          if (isDisposed) {
            return;
          }

          const response = result?.data;
          if (response?.status && !isStatusInProgress(response)) {
            handleResponseComplete(response);
            return;
          }

          if (response?.requestId && response?.conversationId) {
            await waitForDelay(ANYA_POLLING_CONFIG.INITIAL_POLL_DELAY);

            if (isDisposed) {
              return;
            }

            await pollUntilComplete(
              response.conversationId,
              response.requestId,
            );
            return;
          }

          if (!isDisposed) {
            setAgentResponseError("Invalid response from AI service");
            setAgentResponseLoading(false);
          }
        } catch (error: any) {
          if (!error?.cancelled && !isDisposed) {
            setAgentResponseError("Failed to load agent output");
            setAgentResponseLoading(false);
          }
        }
      };

      getAgentResponse();

      return () => {
        isDisposed = true;
        activeRequestCancel();
        if (pollTimeoutId) {
          clearTimeout(pollTimeoutId);
        }
      };
    },
    [findingId, selectedAgent],
  );

  const retryAgentResponse = useCallback(() => {
    setSelectedAgent((previousAgent) =>
      previousAgent ? { ...previousAgent } : previousAgent,
    );
  }, []);

  return {
    agentsLoading,
    agentsError,
    agentOptions,
    updateSearch,
    selectedAgent,
    selectAgent,
    refreshAgents,
    agentResponse,
    agentResponseLoading,
    agentResponseError,
    agentResponseExists,
    retryAgentResponse,
  };
}
